/**
 * SSR Guards Utility
 *
 * Centralized utilities to prevent SSR-related build errors
 * especially important for Vercel builds which are more aggressive
 * about static generation.
 */
import { createLogger } from "./logger";

const logger = createLogger("ssr-guards");

/**
 * Safe window access guard
 * Returns null during SSR/build
 */
export function safeWindow(): Window | null {
  if (typeof window === "undefined") return null;
  return window;
}

/**
 * Safe document access guard
 * Returns null during SSR/build
 */
export function safeDocument(): Document | null {
  if (typeof document === "undefined") return null;
  return document;
}

/**
 * Safe navigator access guard
 * Returns null during SSR/build
 */
export function safeNavigator(): Navigator | null {
  if (typeof navigator === "undefined") return null;
  return navigator;
}

/**
 * Safe localStorage access guard
 * Returns null during SSR/build
 */
export function safeLocalStorage(): Storage | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage;
}

/**
 * Safe sessionStorage access guard
 * Returns null during SSR/build
 */
export function safeSessionStorage(): Storage | null {
  if (typeof sessionStorage === "undefined") return null;
  return sessionStorage;
}

/**
 * Check if we're in a browser environment
 * This is more comprehensive than just checking window
 */
export function isBrowser(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof document !== "undefined" &&
    typeof navigator !== "undefined"
  );
}

/**
 * Check if we're in a Vercel build environment
 */
export function isVercelBuild(): boolean {
  return (
    process.env.VERCEL === "1" ||
    process.env.VERCEL_ENV === "production" ||
    process.env.NODE_ENV === "production"
  );
}

/**
 * Safe execution wrapper for browser-only code
 * Returns null during SSR/build
 */
export function safeBrowserCall<T>(fn: () => T): T | null {
  if (!isBrowser()) return null;
  try {
    return fn();
  } catch (error) {
    logger.warn("Browser call failed", {
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

/**
 * Vercel-safe setTimeout
 * Returns null during SSR/build
 */
export function safeSetTimeout(callback: () => void, delay: number): NodeJS.Timeout | null {
  if (typeof setTimeout === "undefined") return null;
  return setTimeout(callback, delay);
}

/**
 * Vercel-safe setInterval
 * Returns null during SSR/build
 */
export function safeSetInterval(callback: () => void, delay: number): NodeJS.Timeout | null {
  if (typeof setInterval === "undefined") return null;
  return setInterval(callback, delay);
}

/**
 * Debug logging for SSR issues
 * Only logs in development or when explicitly enabled
 */
export function debugSSR(message: string, data?: any) {
  if (process.env.NODE_ENV === "development" || process.env.DEBUG_SSR === "true") {
    logger.debug(message, data || {});
  }
}
